import { isAuthRoute } from '../navigation/authRoutes';
// CORE-498 — pure visibility rule for the Agapella launcher, split out so it can
// be unit-tested without pulling in the React Native / navigation tree.

/**
 * Whether the launcher should be hidden. Authenticated only; never on the
 * full-chat screen (she lives there already).
 *
 * CORE-847 — the widget ships on native too, so the old `isWeb` gate is gone.
 * The two platforms are separate components, though: Metro resolves
 * `AgapellaWidget.native.tsx` over the web file, and THAT one asks
 * {@link ./nativeWidgetVisibility} instead. So this rule is the web widget's,
 * and the native route list is maintained alongside it, not by it — the two
 * hide-sets differ on purpose (see the note there).
 *
 * CORE-496 — the onboarding tour is delivered THROUGH this widget, so an
 * active tour forces it visible: hiding it would leave the tour with no voice.
 * That also covers the tour outliving a stray navigation to another route.
 */
export function shouldHideAgapellaWidget(p: {
  isAuthenticated: boolean;
  route: string | undefined;
  /** Tour running, or her farewell still on screen before it collapses. */
  tourActive: boolean;
}): boolean {
  if (!p.isAuthenticated) return true;
  // CORE-513 — экраны входа и регистрации, и ВЫШЕ проверки тура. Иначе
  // начатое знакомство продолжало бы говорить поверх формы регистрации:
  // авторизованный человек, открывший `/register`, получал её вместе с
  // приветственной модалкой. Тур переживает переход на другой маршрут
  // намеренно, но страница входа — не «другой маршрут», а место, где ему
  // делать нечего.
  if (isAuthRoute(p.route)) return true;
  if (p.tourActive) return false;
  return p.route != null && OWN_CHAT_ROUTES.has(p.route);
}

/**
 * Screens that already own the bottom-right corner with a chat of their own.
 *
 * «Chats» was the original case — she lives there. A running practice is the same
 * problem seen from the other side: reported from the stand, her launcher sits
 * exactly on top of the room's message field, so the two compete for the same
 * click. Hiding her is also the right answer for the moment itself — a practice
 * in progress is not where a floating assistant belongs, and a celebration that
 * would pop over the room waits instead: the event stays pending until it is
 * dismissed, so it appears as soon as the user leaves.
 *
 * CORE-731 — QA (17.08.2026) rejected hiding her on the WHOLE live-session
 * family: on the audience Live page and the green room she vanished entirely,
 * which reads as a regression rather than a fix. Owner decision (17.08): hide her
 * only inside a running practice — kept below — and RETURN her on the Live page
 * (`LiveDetail`) and the green room (`LiveSessionEntry`), where the overlap with
 * the bottom controls is solved by geometry instead (see
 * {@link reservedBottomForRoute}). So those two routes are deliberately NOT here.
 */
const OWN_CHAT_ROUTES: ReadonlySet<string> = new Set([
  'Chats',
  // Открытый диалог (native) — тот же случай, что и «Chats»: экран целиком
  // и есть переписка, плавающей Agapella над чужим диалогом делать нечего,
  // а её иконка садится ровно на поле ввода (CORE-847, native pass).
  'ChatThread',
  // Practice Room / Live session — участник и ведущий.
  'LiveRoomSession',
  'GuestRoom',
  // Мастерская трансляция.
  'LiveBroadcast',
]);

/**
 * Extra clearance, in CSS px, the launcher keeps along the bottom edge on routes
 * that KEEP her but own their own bottom controls (CORE-731). Rather than hiding
 * her there, the widget geometry stacks this band onto the device safe-area
 * bottom inset, which lifts both the default position and any position the user
 * has dragged into the band up above the controls — so the icon is present and
 * tappable and never lands on them:
 *   • `LiveDetail`        — LiveChatPanel «Подарить» button + message composer;
 *   • `LiveSessionEntry`  — bottom «Войти» CTA + audio meter.
 *
 * A single band per route (not a right-only rectangle) is intentional: the green
 * room's audio meter runs the full width of the bottom, and a band also keeps the
 * maths inside the existing inset-aware geometry rather than adding a second
 * clamp axis. The height is approximate and confirmed visually — see the ticket.
 */
const RESERVED_BOTTOM_BY_ROUTE: Readonly<Record<string, number>> = {
  LiveDetail: 96,
  LiveSessionEntry: 96,
};

/**
 * How much bottom clearance the launcher must reserve on `route`, or 0 where it
 * is free to sit in the corner. Pure, so it is unit-tested alongside the
 * visibility rule and fed into the drag geometry as extra bottom inset.
 */
export function reservedBottomForRoute(route: string | undefined): number {
  if (route == null) return 0;
  return RESERVED_BOTTOM_BY_ROUTE[route] ?? 0;
}
